import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import toString from 'lodash/toString';
import { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form';

const PHONE_AREA_CODE_LENGTH = 3;
const PHONE_MIN_LENGTH_FOR_PREFIX = 4;
const PHONE_PREFIX_END = 6;
const PHONE_MIN_LENGTH_FOR_LINE = 7;

export const inputEventHandler = {
  currency: {
    // Used to remove the $ prefix when the input field is blurred.
    blur:
      (
        name: string,
        setValue: UseFormSetValue<any>,
        trigger: UseFormTrigger<any>,
      ) =>
      (event: React.FocusEvent<HTMLInputElement>): void => {
        if (get(event, 'target.value') === '$') {
          setValue(name, '');
        }

        // Trigger validation on blur as we've overridden the default blur behavior.
        trigger(name);
      },
    // Used to format the input field with commas and a $ prefix.
    change:
      (name: string, setValue: UseFormSetValue<any>) =>
      (event: ChangeEvent<HTMLInputElement>): void => {
        setValue(
          name,
          inputEventHandler.currency.formatter(event.target.value),
          // Trigger validation on change as we've overridden the default change behavior.
          // Without shouldValidate react-hook-form isValid state will not be updated as the input value changes.
          { shouldDirty: true, shouldValidate: true },
        );
      },
    // Used to add the $ prefix when the input field is focused.
    focus:
      (name: string, setValue: UseFormSetValue<any>) =>
      (event: React.FocusEvent<HTMLInputElement>): void => {
        if (!get(event, 'target.value')) {
          setValue(name, '$');
        }
      },
    formatter: (value: string | number | null): string => {
      if (!value) {
        return '';
      }

      const numberValue = isNumber(value)
        ? value
        : parseInt(value.replace(/[^.\d]/g, ''), 10);

      if (isNaN(numberValue)) {
        return '';
      }

      return `$${numberValue.toLocaleString()}`;
    },
    parser: (value: string | number | null | undefined): string => {
      const stringValue = toString(value);

      return stringValue.replace(/[^.\d]/g, '');
    },
  },
  number: {
    change:
      (name: string, setValue: UseFormSetValue<any>) =>
      (event: ChangeEvent<HTMLInputElement>): void => {
        setValue(
          name,
          inputEventHandler.number.formatter(event.target.value),
          // Trigger validation on change as we've overridden the default change behavior.
          // Without shouldValidate react-hook-form isValid state will not be updated as the input value changes.
          { shouldDirty: true, shouldValidate: true },
        );
      },
    keydown: (event: KeyboardEvent<HTMLInputElement>): void => {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Tab',
      ];

      const allowedWithControl = ['a', 'c', 'v', 'x'];

      if (
        (event.ctrlKey || event.metaKey) &&
        allowedWithControl.includes(event.key)
      ) {
        return;
      }

      if (allowedKeys.includes(event.key)) {
        return;
      }

      if (isNaN(parseInt(event.key, 10))) {
        event.preventDefault();
      }
    },
    pasteCapture: (event: ClipboardEvent<HTMLInputElement>): void => {
      const value = event.clipboardData.getData('Text');

      if (!/^\d+$/g.test(value)) {
        event.preventDefault();
      }
    },
    formatter: (value: string | number | null): string => {
      if (!value) {
        return '';
      }

      const numberValue = isNumber(value)
        ? value
        : parseInt(value.replace(/[^.\d]/g, ''), 10);

      if (isNaN(numberValue)) {
        return '';
      }

      return numberValue.toLocaleString();
    },
    parser: (value: string | number | null | undefined): string => {
      const stringValue = toString(value);

      return stringValue.replace(/[^.\d]/g, '');
    },
  },
  phoneNumber: {
    formatter: (value: string | number | null | undefined): string => {
      const stringValue = toString(value).replace(/[^\d]/g, '');
      let formattedString = '';

      formattedString +=
        stringValue.length >= PHONE_AREA_CODE_LENGTH
          ? `(${stringValue.substring(0, PHONE_AREA_CODE_LENGTH)}`
          : stringValue;

      formattedString +=
        stringValue.length >= PHONE_MIN_LENGTH_FOR_PREFIX
          ? `) ${stringValue.substring(
              PHONE_AREA_CODE_LENGTH,
              PHONE_PREFIX_END,
            )}`
          : stringValue.substring(PHONE_AREA_CODE_LENGTH);

      formattedString +=
        stringValue.length >= PHONE_MIN_LENGTH_FOR_LINE
          ? `-${stringValue.substring(PHONE_PREFIX_END)}`
          : '';

      return formattedString;
    },
    parser: (value: string | number | null | undefined): string => {
      const stringValue = toString(value);

      return stringValue.replace(/[^\d]/g, '');
    },
    keyDown: (event: KeyboardEvent<HTMLInputElement>): void => {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Tab',
      ];

      const allowedWithControl = ['a', 'c', 'v', 'x'];

      if (
        (event.ctrlKey || event.metaKey) &&
        allowedWithControl.includes(event.key)
      ) {
        return;
      }

      if (allowedKeys.includes(event.key)) {
        return;
      }

      if (isNaN(parseInt(event.key, 10))) {
        event.preventDefault();
      }
    },
  },
};
